/**
 * «Attività utenti» — who signed in, for how long, and what they changed (`docs/adr/9059`, over
 * bata-platform ADR 9029). The pure half: the contract re-exported under the names the screens use,
 * the scope on the link, the reads' queries, the words a row is drawn with, and the exports. The
 * screens are `screens/UserActivityScreen.tsx` (the people) and `screens/UserActivityDetailScreen.tsx`
 * (one person's sessions, or their actions).
 *
 * ══ ADMINISTRATOR ONLY, AND PERSONAL DATA ════════════════════════════════════
 *
 * Every read is `read` on `user_activity`, granted to the administrator alone (not the auditor).
 * Sign-ins, sessions and IP addresses are personal data: the identity broker keeps its events two
 * years, the platform computes this report on read, and this page stores nothing.
 *
 * ══ THE PERIOD IS ON THE LINK, AND HAS NO DEFAULT ════════════════════════════
 *
 *   ?from=YYYY-MM-DD&to=YYYY-MM-DD    the window, both UTC days inclusive, both required, ≤ 92 days
 *   ?sort=last_seen|logins|…          the platform's ranking (its own default is `last_seen`)
 *   ?user=<id>                        one person only
 *
 * CLAUDE.md §3.4: with no period the page asks for one and sends nothing; the quick picks WRITE the
 * dates to the link. A period the platform would refuse (inverted, or longer than 92 days) is
 * refused here first, so the request is never sent.
 *
 * ══ LENGTHS ARE ESTIMATES ════════════════════════════════════════════════════
 *
 * The broker logs no event when a session expires, so a session with no sign-out ends, by
 * estimate, at its last event plus the idle timeout, never past its start plus the maximum
 * lifespan; an open one runs to now. `estimatedSeconds`, `totalSessionSeconds` and
 * `medianSessionSeconds` are those estimates and are labelled so wherever they are shown;
 * `observedSeconds` (first to last event) is what was seen.
 */

import { MAX_PAGES, callOperation, reportPageRead } from '@/api';
import {
  readLastAccess, readUserActions, readUserActivity, readUserSessions,
  type ReadUserActionsParams, type ReadUserActivityParams, type ReadUserSessionsParams,
  type SessionEnding, type SessionRule, type UserActionGroup, type UserActionPage, type UserActivityPage, type UserActivityRow,
  type UserActivitySort, type UserSession, type UserSessionPage,
} from '@/api/generated';
import {
  NOT_PUBLISHED, NO_PRINCIPAL, buildVersion,
  type ExportCell, type ExportColumn, type ExportRequest, type ExportTranslate, type ProvenanceFilter,
} from '@/export';
import { TABLE_ROW_CAP } from '@/planning';

import type { ExportScope } from '../manifest';

import { isCalendarDate, lastDays, periodDays } from './activity';

export type {
  SessionEnding, SessionRule, UserActionGroup, UserActionPage, UserActivityPage, UserActivityRow, UserActivitySort, UserSession,
  UserSessionPage,
};

/** The generated operations — what the page, the export and the Users screen's column read. */
export const USER_ACTIVITY_READ = readUserActivity;
export const USER_SESSIONS_READ = readUserSessions;
export const USER_ACTIONS_READ = readUserActions;
export const LAST_ACCESS_READ = readLastAccess;

/** The paths the platform serves them at — what the re-pin tripwire looks for in the spec. */
export const USER_ACTIVITY_PATH_IN_SPEC = '/api/v1/admin/user-activity';

// ── the link ─────────────────────────────────────────────────────────────────

export const USER_ACTIVITY_ROUTE = '/foundations/user-activity';
export const USER_FROM_PARAM = 'from';
export const USER_TO_PARAM = 'to';
export const USER_SORT_PARAM = 'sort';
export const USER_FILTER_PARAM = 'user';

/** The platform's bound on the window, in days (bata-platform ADR 9029, `maximumDays`). */
export const USER_ACTIVITY_MAX_DAYS = 92;

/** The quick picks — «Ultimi 7 / 30 / 90 giorni»: each within the platform's 92. */
export const USER_QUICK_PERIOD_DAYS = [7, 30, 90] as const;

/** The platform's page bound, and the table's cap (owner ruling 2026-10-01, ADR 9034): fifty. */
export const USER_ACTIVITY_LIMIT = Math.min(TABLE_ROW_CAP, 50);

/** The platform's ranking, in its order. `user-activity.test.tsx` holds it equal to the pinned enum. */
export const USER_ACTIVITY_SORTS = ['last_seen', 'logins', 'failed_logins', 'session_time', 'actions'] as const satisfies readonly UserActivitySort[];

export interface UserActivityPeriod {
  readonly from: string;
  readonly to: string;
}

/** Why a period on the link is not read: refused here, before any request. */
export type PeriodProblem = 'inverted' | 'tooLong';

/** The period a link states — all one person's pages read. */
export interface UserPeriodScope {
  /** Both dates, valid and within the bound — or `null`, and nothing is read. */
  readonly period: UserActivityPeriod | null;
  /** Both dates were on the link but are not a window the platform reads. */
  readonly problem: { readonly period: UserActivityPeriod; readonly kind: PeriodProblem } | null;
}

/** The people page's scope: the period, the ranking and the one-person filter. */
export interface UserActivityScope extends UserPeriodScope {
  /** `null`: the platform's own ranking (`last_seen`). */
  readonly sort: UserActivitySort | null;
  readonly user: string | null;
}

function param(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim() ?? '';
  return value === '' ? null : value;
}

export function isUserActivitySort(value: string): value is UserActivitySort {
  return (USER_ACTIVITY_SORTS as readonly string[]).includes(value);
}

/** What is wrong with a period, or `null` when the platform will read it. */
export function periodProblem(period: UserActivityPeriod): PeriodProblem | null {
  if (period.to < period.from) return 'inverted';
  return periodDays(period) > USER_ACTIVITY_MAX_DAYS ? 'tooLong' : null;
}

/**
 * The period a search string states. Pure: a string in, never `window`, no default. A malformed
 * date is absent; a period inverted or longer than 92 days is a `problem`, said on the page, never
 * sent.
 */
export function readUserPeriod(search: string): UserPeriodScope {
  const params = new URLSearchParams(search);
  const from = param(params, USER_FROM_PARAM);
  const to = param(params, USER_TO_PARAM);
  const both = from !== null && to !== null && isCalendarDate(from) && isCalendarDate(to) ? { from, to } : null;
  const kind = both === null ? null : periodProblem(both);
  return {
    period: both !== null && kind === null ? both : null,
    problem: both !== null && kind !== null ? { period: both, kind } : null,
  };
}

/** The people page's scope: the period, and the ranking and the filter when the link names them. */
export function readUserActivityScope(search: string): UserActivityScope {
  const params = new URLSearchParams(search);
  const sort = param(params, USER_SORT_PARAM);
  return {
    ...readUserPeriod(search),
    sort: sort !== null && isUserActivitySort(sort) ? sort : null,
    user: param(params, USER_FILTER_PARAM),
  };
}

export type UserActivityQuery = NonNullable<ReadUserActivityParams['query']> & { readonly from: string; readonly to: string };
export type UserWindowQuery = NonNullable<ReadUserSessionsParams['query']> & { readonly from: string; readonly to: string };

/** The people read's query, or `null` while the period is missing — nothing is sent. */
export function userActivityQuery(scope: UserActivityScope, limit = USER_ACTIVITY_LIMIT): UserActivityQuery | null {
  if (scope.period === null) return null;
  return {
    from: scope.period.from,
    to: scope.period.to,
    limit,
    ...(scope.sort === null ? {} : { sort: scope.sort }),
    ...(scope.user === null ? {} : { user: scope.user }),
  };
}

/** One person's sessions or actions read, or `null` while the period is missing. */
export function userWindowQuery(scope: UserPeriodScope, limit = USER_ACTIVITY_LIMIT): UserWindowQuery | null {
  return scope.period === null ? null : { from: scope.period.from, to: scope.period.to, limit };
}

/** The quick pick of `days`, ending today (UTC). Re-exported so the page has one import. */
export function lastDaysPeriod(days: number, now: number): UserActivityPeriod {
  return lastDays(days, now);
}

/** The person's page — their sessions — over the same period (G2.2: the drill keeps its context). */
export function userDetailHref(userId: string, period: UserActivityPeriod, view: 'sessions' | 'actions' = 'sessions'): string {
  const query = new URLSearchParams({ [USER_FROM_PARAM]: period.from, [USER_TO_PARAM]: period.to }).toString();
  const base = `${USER_ACTIVITY_ROUTE}/${encodeURIComponent(userId)}`;
  return `${view === 'actions' ? `${base}/actions` : base}?${query}`;
}

/** The person and the view a drill path names, or `null` for a path that is not one. */
export function readUserDetailPath(pathname: string): { readonly userId: string; readonly view: 'sessions' | 'actions' } | null {
  const match = /^\/foundations\/user-activity\/([^/]+)(\/actions)?\/?$/.exec(pathname);
  if (match === null || match[1] === undefined) return null;
  let userId: string;
  try {
    userId = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  return userId.trim() === '' ? null : { userId, view: match[2] === undefined ? 'sessions' : 'actions' };
}

// ── the words ────────────────────────────────────────────────────────────────

/**
 * A person as the page may name them: the name on their profile, else their sign-in name, else
 * their id (`named: false`, drawn in mono) — never invented.
 */
export function personWords(row: Pick<UserActivityRow, 'userId' | 'username' | 'displayName'>): { readonly name: string; readonly named: boolean } {
  const display = row.displayName?.trim() ?? '';
  if (display !== '') return { name: display, named: true };
  const username = row.username?.trim() ?? '';
  if (username !== '') return { name: username, named: true };
  return { name: row.userId, named: false };
}

/** A length in words: «meno di 1 min», «45 min», «2 h 15 min». Floors to the minute. */
export function durationWords(seconds: number): { readonly key: string; readonly values: Readonly<Record<string, number>> } {
  if (!Number.isFinite(seconds) || seconds < 60) return { key: 'foundations.userActivity.duration.underMinute', values: {} };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { key: 'foundations.userActivity.duration.minutes', values: { minutes } };
  return { key: 'foundations.userActivity.duration.hoursMinutes', values: { hours: Math.floor(minutes / 60), minutes: minutes % 60 } };
}

/** How each session ended, in words — spelled out, so the catalogue check sees every key. */
export const SESSION_ENDING_KEY: Readonly<Record<SessionEnding, string>> = {
  logout: 'foundations.userActivity.ending.logout',
  idle_timeout: 'foundations.userActivity.ending.idleTimeout',
  max_lifespan: 'foundations.userActivity.ending.maxLifespan',
  open: 'foundations.userActivity.ending.open',
};

/** The ranking's words, in the order the select offers them. */
export const USER_SORT_LABEL_KEY: Readonly<Record<UserActivitySort, string>> = {
  last_seen: 'foundations.userActivity.sort.lastSeen',
  logins: 'foundations.userActivity.sort.logins',
  failed_logins: 'foundations.userActivity.sort.failedLogins',
  session_time: 'foundations.userActivity.sort.sessionTime',
  actions: 'foundations.userActivity.sort.actions',
};

/** The platform's own ranking when the link names none. */
export const DEFAULT_PLATFORM_SORT: UserActivitySort = 'last_seen';

// ── the problems the reads answer ────────────────────────────────────────────

export const IDENTITY_EVENTS_UNAVAILABLE = 'platform.admin.identity_events_unavailable';
export const IDENTITY_EVENTS_TOO_MANY = 'platform.admin.identity_events_too_many';
export const USER_ACTIVITY_WINDOW_INVALID = 'platform.admin.user_activity_window_invalid';
export const USER_ACTIVITY_UNAVAILABLE = 'platform.admin.user_activity_unavailable';
export const LAST_ACCESS_USERS_INVALID = 'platform.admin.last_access_users_invalid';

/** The broker credential is not on this tier yet — a designed state, not a fault. */
export const NOT_CONFIGURED = 'not_configured';

// ── the exports ──────────────────────────────────────────────────────────────

export const USER_ACTIVITY_EXPORT_ID = 'foundations.userActivity.table';
export const USER_SESSIONS_EXPORT_ID = 'foundations.userActivity.sessions';
export const USER_ACTIONS_EXPORT_ID = 'foundations.userActivity.actions';

const PLATFORM_VERSION = buildVersion();

const text = (value: string | null): ExportCell => ({ kind: 'text', value: value ?? '' });
const instant = (value: string | null): ExportCell => (value === null ? text(null) : { kind: 'date', iso: value });
const integer = (value: number): ExportCell => ({ kind: 'integer', raw: value });

/** A code in words through `key`, in the workbook only; the CSV keeps the code. */
const wordsOf = (key: (code: string) => string | null) => (code: string, translate: ExportTranslate): string => {
  const found = key(code);
  if (found === null) return code;
  const words = translate(found);
  return words === '' || words === found ? code : words;
};

function provenance(scope: UserPeriodScope, filters: readonly ProvenanceFilter[], measure: string, definition: string): ExportRequest['provenance'] {
  return {
    filters,
    hierarchyNode: NOT_PUBLISHED,
    timeWindow: scope.period === null ? NOT_PUBLISHED : { from: scope.period.from, to: scope.period.to },
    scenario: NOT_PUBLISHED,
    planVersionId: NOT_PUBLISHED,
    measureDefinitions: [{ measure, definition, registerVersion: '' }],
    generatedAt: new Date().toISOString(),
    generatedBy: NO_PRINCIPAL,
    platformVersion: PLATFORM_VERSION,
  };
}

function request(id: string, titleKey: string, columns: readonly ExportColumn[], cells: ExportCell[][], scope: UserPeriodScope,
  filters: readonly ProvenanceFilter[], measure: string, definition: string): ExportRequest {
  return {
    id,
    titleKey,
    columns,
    rows: () => cells,
    rowCount: cells.length,
    provenance: provenance(scope, filters, measure, definition),
    perimeter: { filters, sort: [], visibleColumns: columns.map((column) => column.key), drillPath: [], densityMode: 'compact' },
  };
}

/** The people's columns. Lengths in seconds, as the platform publishes them, and named estimates. */
export const USER_ACTIVITY_EXPORT_COLUMNS: readonly ExportColumn[] = [
  { key: 'user_id', headerKey: 'foundations.userActivity.export.userId', kind: 'text', descriptionKey: 'foundations.columnGuide.userActivity.userId' },
  { key: 'username', headerKey: 'foundations.userActivity.export.username', kind: 'text', descriptionKey: 'foundations.columnGuide.userActivity.username' },
  { key: 'display_name', headerKey: 'foundations.userActivity.export.displayName', kind: 'text', descriptionKey: 'foundations.columnGuide.userActivity.displayName' },
  { key: 'last_login_at', headerKey: 'foundations.userActivity.column.lastLogin', kind: 'date', descriptionKey: 'foundations.columnGuide.userActivity.lastLogin' },
  { key: 'last_seen_at', headerKey: 'foundations.userActivity.column.lastSeen', kind: 'date', descriptionKey: 'foundations.columnGuide.userActivity.lastSeen' },
  { key: 'logins', headerKey: 'foundations.userActivity.column.logins', kind: 'integer', descriptionKey: 'foundations.help.userActivityScreen.colLogins' },
  { key: 'failed_logins', headerKey: 'foundations.userActivity.column.failedLogins', kind: 'integer', descriptionKey: 'foundations.help.userActivityScreen.colFailedLogins' },
  { key: 'sessions', headerKey: 'foundations.userActivity.column.sessions', kind: 'integer', descriptionKey: 'foundations.columnGuide.userActivity.sessions' },
  { key: 'open_sessions', headerKey: 'foundations.userActivity.export.openSessions', kind: 'integer', descriptionKey: 'foundations.columnGuide.userActivity.openSessions' },
  { key: 'total_session_seconds_estimated', headerKey: 'foundations.userActivity.export.totalSeconds', kind: 'integer', unit: 's', descriptionKey: 'foundations.columnGuide.userActivity.totalSeconds' },
  { key: 'median_session_seconds_estimated', headerKey: 'foundations.userActivity.export.medianSeconds', kind: 'integer', unit: 's', descriptionKey: 'foundations.columnGuide.userActivity.medianSeconds' },
  { key: 'actions', headerKey: 'foundations.userActivity.column.actions', kind: 'integer', descriptionKey: 'foundations.help.userActivityScreen.colActions' },
];

function scopeFilters(scope: UserActivityScope): readonly ProvenanceFilter[] {
  const pairs: readonly (readonly [string, string | null])[] = [['user', scope.user], ['sort', scope.sort]];
  return pairs.flatMap(([field, value]) => (value === null ? [] : [{ field, operator: '=', value }]));
}

export function userActivityRequest(rows: readonly UserActivityRow[], scope: UserActivityScope): ExportRequest {
  const cells: ExportCell[][] = rows.map((row) => [
    text(row.userId), text(row.username), text(row.displayName),
    instant(row.lastLoginAt), instant(row.lastSeenAt),
    integer(row.logins), integer(row.failedLogins), integer(row.sessions), integer(row.openSessions),
    integer(row.totalSessionSeconds),
    // No session ended in the period: no median, and not a zero.
    row.medianSessionSeconds === null ? { kind: 'missing', state: 'not-reported' } : integer(row.medianSessionSeconds),
    integer(row.actions),
  ]);
  return request(USER_ACTIVITY_EXPORT_ID, 'foundations.userActivity.caption', USER_ACTIVITY_EXPORT_COLUMNS, cells, scope, scopeFilters(scope),
    'user_activity_row',
    'One person over the period: sign-ins and failed sign-ins, sessions started (and still open), the ESTIMATED total and median session length in seconds (no sign-out: last event plus the idle timeout, never past start plus the maximum lifespan), and state changes in the audit record, as GET /admin/user-activity publishes it (bata-platform ADR 9029).');
}

export const USER_SESSIONS_EXPORT_COLUMNS: readonly ExportColumn[] = [
  { key: 'session_id', headerKey: 'foundations.userActivity.export.sessionId', kind: 'text', descriptionKey: 'foundations.columnGuide.userActivity.sessionId' },
  { key: 'started_at', headerKey: 'foundations.userActivity.column.startedAt', kind: 'date', descriptionKey: 'foundations.help.userSessionsScreen.colStartedAt' },
  { key: 'last_seen_at', headerKey: 'foundations.userActivity.column.lastSeen', kind: 'date', descriptionKey: 'foundations.columnGuide.userActivity.sessionLastSeen' },
  { key: 'ended_at', headerKey: 'foundations.userActivity.column.endedAt', kind: 'date', descriptionKey: 'foundations.columnGuide.userActivity.endedAt' },
  {
    key: 'ended_by', headerKey: 'foundations.userActivity.column.endedBy', kind: 'label',
    display: wordsOf((code) => (code in SESSION_ENDING_KEY ? SESSION_ENDING_KEY[code as SessionEnding] : null)),
    descriptionKey: 'foundations.columnGuide.userActivity.endedBy',
  },
  { key: 'estimated_seconds', headerKey: 'foundations.userActivity.export.estimatedSeconds', kind: 'integer', unit: 's', descriptionKey: 'foundations.columnGuide.userActivity.estimatedSeconds' },
  { key: 'observed_seconds', headerKey: 'foundations.userActivity.export.observedSeconds', kind: 'integer', unit: 's', descriptionKey: 'foundations.columnGuide.userActivity.observedSeconds' },
  { key: 'ip_address', headerKey: 'foundations.userActivity.column.ip', kind: 'text', descriptionKey: 'foundations.help.userSessionsScreen.colIp' },
  { key: 'client', headerKey: 'foundations.userActivity.column.client', kind: 'text', descriptionKey: 'foundations.help.userSessionsScreen.colClient' },
  { key: 'refreshes', headerKey: 'foundations.userActivity.column.refreshes', kind: 'integer', descriptionKey: 'foundations.help.userSessionsScreen.colRefreshes' },
];

export function userSessionsRequest(userId: string, rows: readonly UserSession[], scope: UserPeriodScope): ExportRequest {
  const cells: ExportCell[][] = rows.map((row) => [
    text(row.sessionId), instant(row.startedAt), instant(row.lastSeenAt), instant(row.endedAt),
    { kind: 'label', value: row.endedBy, code: row.endedBy },
    integer(row.estimatedSeconds), integer(row.observedSeconds),
    text(row.ipAddress), text(row.client), integer(row.refreshes),
  ]);
  return request(USER_SESSIONS_EXPORT_ID, 'foundations.userActivity.sessions.caption', USER_SESSIONS_EXPORT_COLUMNS, cells, scope,
    [{ field: 'user', operator: '=', value: userId }],
    'user_session',
    'One session of one person, rebuilt from the identity broker\'s events: started, last seen, ended (empty while open) and how; the ESTIMATED length in seconds (to the estimated end, or to now while open) and the OBSERVED one (first to last event); the sign-in\'s IP address and client, and the token refreshes, as GET /admin/user-activity/{user_id}/sessions publishes it.');
}

export const USER_ACTIONS_EXPORT_COLUMNS: readonly ExportColumn[] = [
  { key: 'method', headerKey: 'foundations.userActivity.column.method', kind: 'text', descriptionKey: 'foundations.columnGuide.userActivity.method' },
  { key: 'route', headerKey: 'foundations.userActivity.column.route', kind: 'text', descriptionKey: 'foundations.columnGuide.userActivity.route' },
  { key: 'object_kind', headerKey: 'foundations.userActivity.column.objectKind', kind: 'text', descriptionKey: 'foundations.help.userActionsScreen.colObjectKind' },
  { key: 'count', headerKey: 'foundations.userActivity.column.count', kind: 'integer', descriptionKey: 'foundations.help.userActionsScreen.colCount' },
  { key: 'last_at', headerKey: 'foundations.userActivity.column.lastAt', kind: 'date', descriptionKey: 'foundations.help.userActionsScreen.colLastAt' },
];

export function userActionsRequest(userId: string, rows: readonly UserActionGroup[], scope: UserPeriodScope): ExportRequest {
  const cells: ExportCell[][] = rows.map((row) => [
    text(row.method), text(row.route), text(row.objectKind), integer(row.count), instant(row.lastAt),
  ]);
  return request(USER_ACTIONS_EXPORT_ID, 'foundations.userActivity.actions.caption', USER_ACTIONS_EXPORT_COLUMNS, cells, scope,
    [{ field: 'user', operator: '=', value: userId }],
    'user_action_group',
    'One kind of state change by one person in the period: the method and route template, the object kind, how many and the newest, from the platform\'s audit record (the person\'s own profile and session writes are pseudonymised there and not counted), as GET /admin/user-activity/{user_id}/actions publishes it.');
}

// ── every page of a read ─────────────────────────────────────────────────────

interface Paged<T> {
  readonly items: readonly T[];
  readonly nextCursor: string | null;
}

/**
 * Follow `nextCursor` to the end, bounded as the transport's own walk is (`MAX_PAGES`), failing
 * loudly past it rather than leaving part of the period out of the file.
 */
async function walk<T>(fetchPage: (cursor: string | null) => Promise<Paged<T>>, what: string): Promise<readonly T[]> {
  const rows: T[] = [];
  let cursor: string | null = null;
  let pages = 0;
  do {
    const page: Paged<T> = await fetchPage(cursor);
    rows.push(...page.items);
    // The file's walk: the download's progress counts its pages (`docs/adr/9074`).
    reportPageRead(page.items.length);
    cursor = page.nextCursor;
    pages += 1;
    if (pages > MAX_PAGES && cursor !== null) throw new Error(`${what}: more than ${MAX_PAGES} pages; narrow the period`);
  } while (cursor !== null);
  return rows;
}

export function readAllUserActivity(query: UserActivityQuery, read = USER_ACTIVITY_READ): Promise<readonly UserActivityRow[]> {
  const base = { ...query, limit: USER_ACTIVITY_LIMIT };
  return walk((cursor) => callOperation(read, { query: cursor === null ? base : { ...base, cursor } }), 'user activity export');
}

export function readAllUserSessions(userId: string, query: UserWindowQuery, read = USER_SESSIONS_READ): Promise<readonly UserSession[]> {
  const base = { ...query, limit: USER_ACTIVITY_LIMIT };
  return walk((cursor) => callOperation(read, { path: { user_id: userId }, query: cursor === null ? base : { ...base, cursor } }), 'user sessions export');
}

export function readAllUserActions(userId: string, query: UserWindowQuery, read = USER_ACTIONS_READ): Promise<readonly UserActionGroup[]> {
  const base: NonNullable<ReadUserActionsParams['query']> = { ...query, limit: USER_ACTIVITY_LIMIT };
  return walk((cursor) => callOperation(read, { path: { user_id: userId }, query: cursor === null ? base : { ...base, cursor } }), 'user actions export');
}

/** The people route's export (G2 test d): every page of the read the link states. */
export async function userActivityExport(scope: ExportScope): Promise<ExportRequest> {
  const activity = readUserActivityScope(scope.search);
  const query = userActivityQuery(activity);
  if (query === null) throw new Error('user activity export requires a period (?from=&to=, at most 92 days); none is on the link');
  return userActivityRequest(await readAllUserActivity(query), activity);
}

function detailScope(scope: ExportScope, view: 'sessions' | 'actions'): { readonly userId: string; readonly activity: UserPeriodScope; readonly query: UserWindowQuery } {
  const detail = readUserDetailPath(scope.path);
  if (detail === null || detail.view !== view) throw new Error(`user ${view} export: ${scope.path} names no person`);
  const activity = readUserPeriod(scope.search);
  const query = userWindowQuery(activity);
  if (query === null) throw new Error(`user ${view} export requires a period (?from=&to=, at most 92 days); none is on the link`);
  return { userId: detail.userId, activity, query };
}

/** One person's sessions route's export: every session of the period. */
export async function userSessionsExport(scope: ExportScope): Promise<ExportRequest> {
  const { userId, activity, query } = detailScope(scope, 'sessions');
  return userSessionsRequest(userId, await readAllUserSessions(userId, query), activity);
}

/** One person's actions route's export: every kind of act of the period. */
export async function userActionsExport(scope: ExportScope): Promise<ExportRequest> {
  const { userId, activity, query } = detailScope(scope, 'actions');
  return userActionsRequest(userId, await readAllUserActions(userId, query), activity);
}
